/**
 * Temakatalogen — ren data, inga DOM-beroenden.
 *
 * Ligger i en egen .mjs för att temavalidatorn (scripts/validate_themes.mjs)
 * ska kunna importera den med vanliga node. package.json saknar
 * "type": "module", så node läser en .js som CommonJS och kraschar på
 * `export` — validatorn dog innan ett enda tema kontrollerades.
 *
 * Att i stället döpa om themes/index.js hade inte gått: CRA filtrerar bort
 * mjs ur jests moduleFileExtensions, så katalogimporten `from '../themes'`
 * hade slutat lösas i testkörningen. Därför ligger datan här och
 * themes/index.js återexporterar den — app, jest och node får alla samma
 * lista utan att någon importsökväg ändras.
 *
 * Allt som rör document (ensureThemeFont) bor kvar i index.js.
 */

export const POLARITY_DARK = 'dark';
export const POLARITY_LIGHT = 'light';

/**
 * Grupperingen i temaväljaren. Etiketterna översätts via i18n-nycklarna
 * `settings.themeGroup.<id>`; temanamnen översätts INTE — Mirage heter Mirage
 * på alla språk.
 */
export const THEME_GROUPS = ['base', 'maps', 'wildcards'];

export const THEMES = [
  {
    id: 'dark',
    label: 'Mörkt',
    group: 'base',
    polarity: POLARITY_DARK,
    charts: ['#3b82f6', '#e06000', '#16a34a'],
  },
  {
    id: 'light',
    label: 'Ljust',
    group: 'base',
    polarity: POLARITY_LIGHT,
    charts: ['#3b82f6', '#e06000', '#16a34a'],
  },
  {
    id: 'ancient',
    label: 'Ancient',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Templens guld leder — det är den enda färgen som lyser i djungelskuggan.
    charts: ['#dda520', '#5ab5a8', '#c96a5c'],
  },
  {
    id: 'anubis',
    label: 'Anubis',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Nilens turkos, sandstensguld, bränd lera. Turkosen är dragen mot blått
    // för att inte kollidera med Mirages (ΔE 26 mellan dem).
    charts: ['#35a8c4', '#dcb648', '#c9765c'],
  },
  {
    id: 'cache',
    label: 'Cache',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Strålningsgult först — gruppens enda kalla guld, och kartans signatur.
    charts: ['#c9c94a', '#82bd6a', '#6bb2c4'],
  },
  {
    id: 'dust2',
    label: 'Dust 2',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Himmelsblått först: det är enda kalla färgen i ett tema av bara sand,
    // så den bär serien som ska sticka ut.
    charts: ['#6fb7e0', '#e0894a', '#9ec46a'],
  },
  {
    id: 'inferno',
    label: 'Inferno',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Tegel, cypress, himmel — kartans tre faktiska ytor.
    charts: ['#d97a4e', '#7fae62', '#5fb0c9'],
  },
  {
    id: 'italy',
    label: 'Italy',
    group: 'maps',
    polarity: POLARITY_DARK,
    charts: ['#d19a3e', '#6fa368', '#d6614f'],
  },
  {
    id: 'mirage',
    label: 'Mirage',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Markisturkos, mässingsguld, bränd lera. Ingen av dem är röd: en
    // sink-stapel ska inte se ut som ett misslyckande bara för att temat
    // har varma toner.
    charts: ['#3fb8a8', '#e0b64a', '#c98b6a'],
  },
  {
    id: 'nuke',
    label: 'Nuke',
    group: 'maps',
    polarity: POLARITY_DARK,
    charts: ['#54c9d6', '#e8c547', '#e28544'],
  },
  {
    id: 'office',
    label: 'Office',
    group: 'maps',
    // Enda LJUSA karttemat. Diagramfärgerna är nedtonade i ljushet för att
    // klara 3:1 mot en ljus kortyta — ett mörkt temas serie hade legat
    // runt 1,5:1 här.
    polarity: POLARITY_LIGHT,
    charts: ['#1a5fa8', '#8f5320', '#2c6b4a'],
  },
  {
    id: 'overpass',
    label: 'Overpass',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Parkgrönt först — enda karttemat där grönt är huvudfärg och inte bara
    // "lyckat".
    charts: ['#6fb583', '#6aa8d4', '#dd8f4e'],
  },
  {
    id: 'train',
    label: 'Train',
    group: 'maps',
    polarity: POLARITY_DARK,
    // Snöblått leder. Planen gav både Train och Vertigo orange signatur;
    // byggda så blev de samma tema, så Train leds av snön i stället.
    charts: ['#9fc4dc', '#c8804a', '#d4b566'],
  },
  {
    id: 'vertigo',
    label: 'Vertigo',
    group: 'maps',
    polarity: POLARITY_DARK,
    charts: ['#e8722c', '#5aa9e0', '#6fbd85'],
  },
  // Wildcards. Ingen av dem har fontUrl: typsnitten är självhostade med
  // @font-face i respektive temas CSS, eftersom size-adjust inte går att sätta
  // på ett stylesheet man inte äger. Se filerna.
  {
    id: 'ember',
    label: 'Ember',
    group: 'wildcards',
    polarity: POLARITY_DARK,
    // Legendary, magic, set — föremålskvaliteterna, inte påhittade färger.
    charts: ['#e08a3c', '#8a93ec', '#6fbf5c'],
  },
  {
    id: 'epic',
    label: 'Epic',
    group: 'wildcards',
    polarity: POLARITY_DARK,
    // Lila loot först — temats signatur och det som skiljer det från Vertigo.
    charts: ['#b07de8', '#e0b552', '#5fc45f'],
  },
  {
    id: 'fairway',
    label: 'Fairway',
    group: 'wildcards',
    // Enda LJUSA temat som leds av grönt — Overpass gör det i mörkt läge.
    polarity: POLARITY_LIGHT,
    charts: ['#2c6b1f', '#15607f', '#a24d15'],
  },
  {
    id: 'lemmings',
    label: 'Lemmings',
    group: 'wildcards',
    polarity: POLARITY_DARK,
    // Amigapalettens omättade toner finns inte — allt är rent och hårt.
    charts: ['#4fd44f', '#5f9cf5', '#f0913c'],
  },
  {
    id: 'mushroom-kingdom',
    label: 'Mushroom Kingdom',
    group: 'wildcards',
    polarity: POLARITY_LIGHT,
    charts: ['#2352c9', '#a53d06', '#046a1a'],
  },
  {
    id: 'ringworld',
    label: 'Ringworld',
    group: 'wildcards',
    polarity: POLARITY_DARK,
    // Cyanen först — signaturen, och det som håller temat borta från Nuke.
    charts: ['#7fd4e8', '#9ecf55', '#e8823c'],
  },
  {
    id: 'surface',
    label: 'Surface',
    group: 'wildcards',
    // Enda VARMA ljusa temat. Diagramfärgerna är nedtonade i ljushet för att
    // klara 3:1 mot en ljus kortyta.
    polarity: POLARITY_LIGHT,
    charts: ['#0f6b83', '#9e4f1e', '#236640'],
  },
  {
    id: 'tibia',
    label: 'Tibia',
    group: 'wildcards',
    polarity: POLARITY_DARK,
    // Guldmynten först — renare och mättare än Caches strålningsgula.
    charts: ['#f0c832', '#6fc040', '#5fa8dc'],
  },
];

/** Snabbuppslagning per id. */
export const THEME_BY_ID = Object.fromEntries(THEMES.map((t) => [t.id, t]));

